import React, { forwardRef, useCallback, useState, useRef, useMemo } from 'react';
import { DivWithTextAreaSuggestions } from '../DivWithTextAreaSuggestions/DivWithTextAreaSuggestions';
import { CommentSubmitButtons } from '../CommentSubmitButtons/CommentSubmitButtons';
import { AttachmentCarousel } from '../AttachmentCarousel/AttachmentCarousel';
import type {
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';
import { getActionBarConfigForEdit } from '../../utils/actionBarUtils';
import {
  useEditModeFileUpload,
  getActionBarConfigWithEditFileUpload,
  getFileUploadInputPropsForEdit
} from '../../containers/CommentForm/useEditModeFileUpload';

interface CommentEditModeProps
  extends React.ComponentProps<typeof DivWithTextAreaSuggestions> {
  editText: string;
  onTextChange: (value: string) => void;
  onSubmit: (
    value: string,
    mentions?: CommentMention[],
    attachments?: (CommentAttachment | File)[]
  ) => void;
  onCancel: () => void;
  initialMentions?: CommentMention[];
  /** Existing attachments when editing (shown in bar; user can remove or add new). */
  initialAttachments?: (CommentAttachment | File)[];
  submitOnBlur?: boolean;
}

export const CommentEditMode = forwardRef<
  HTMLTextAreaElement,
  CommentEditModeProps
>(
  (
    {
      editText,
      onTextChange,
      onSubmit,
      onCancel,
      initialMentions = [],
      initialAttachments,
      submitOnBlur = false,
      ...restProps
    },
    ref
  ) => {
    const [currentMentions, setCurrentMentions] =
      useState<CommentMention[]>(initialMentions);
    const formContainerRef = useRef<HTMLDivElement>(null);

    const {
      actionBarConfig: actionBarConfigForEdit,
      showCommentSubmitButtons
    } = useMemo(
      () =>
        getActionBarConfigForEdit(restProps.actionBarConfig, {
          submitOnBlur,
          onCancelClick: onCancel
        }),
      [restProps.actionBarConfig, submitOnBlur, onCancel]
    );

    const fileUpload = useEditModeFileUpload(
      initialAttachments,
      actionBarConfigForEdit,
      onCancel
    );

    const submitWithAttachments = useCallback(() => {
      const attachments = fileUpload?.getFinalAttachments?.();
      onSubmit(editText, currentMentions, attachments);
    }, [onSubmit, editText, currentMentions, fileUpload]);

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onTextChange(event.target.value);
      },
      [onTextChange]
    );

    const handleContainerClick = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleMentionsChange = useCallback(
      (newMentions: CommentMention[]) => {
        setCurrentMentions(newMentions);
      },
      []
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          submitWithAttachments();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      },
      [submitWithAttachments, onCancel]
    );

    const handleBlur = useCallback(() => {
      if (!submitOnBlur) return;
      const runAfterFocusSettled = () => {
        const active = document.activeElement;
        if (formContainerRef.current?.contains(active)) return;
        submitWithAttachments();
      };
      setTimeout(runAfterFocusSettled, 0);
    }, [submitWithAttachments, submitOnBlur]);

    const handleSubmitButton = useCallback(() => {
      submitWithAttachments();
    }, [submitWithAttachments]);

    const effectiveActionBarConfig =
      fileUpload != null
        ? getActionBarConfigWithEditFileUpload(
            actionBarConfigForEdit,
            fileUpload
          )
        : actionBarConfigForEdit;

    const fileUploadInputProps =
      fileUpload != null ? getFileUploadInputPropsForEdit(fileUpload) : {};

    return (
      <div onClick={handleContainerClick}>
        <DivWithTextAreaSuggestions
          {...restProps}
          {...fileUploadInputProps}
          ref={ref}
          value={editText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onSubmit={() => submitWithAttachments()}
          onMentionsChange={handleMentionsChange}
          initialMentions={initialMentions}
          placeholder="@mention users or edit comment"
          className="comment-text__edit-input"
          wrapperClassName="comment-text__edit-container"
          enableMentions={true}
          showSubmitButton={false}
          showClearButton={false}
          actionBarConfig={effectiveActionBarConfig}
          containerRef={formContainerRef}
        />
        {fileUpload?.carouselOpen && fileUpload.attachments.length > 0 && (
          <AttachmentCarousel
            attachments={fileUpload.attachments}
            initialIndex={fileUpload.carouselIndex}
            onClose={() => fileUpload.setCarouselOpen(false)}
          />
        )}
        {showCommentSubmitButtons && (
          <CommentSubmitButtons
            onCancel={onCancel}
            onSubmit={handleSubmitButton}
          />
        )}
      </div>
    );
  }
);
